import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  UserPlus,
  Search,
  Edit2,
  Trash2,
  X,
  CheckCircle,
  Phone,
  MapPin,
  User
} from 'lucide-react';

const PageContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  gap: 1rem;

  .header-text h1 {
    font-size: 1.875rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 0.5rem 0;
  }

  .header-text p {
    color: #64748b;
    margin: 0;
  }
`;

const SearchBar = styled.div`
  position: relative;
  margin-bottom: 1.5rem;

  svg {
    position: absolute;
    left: 1rem;
    top: 50%;
    transform: translateY(-50%);
    color: #9ca3af;
  }

  input {
    width: 100%;
    padding: 0.75rem 1rem 0.75rem 2.75rem;
    border: 2px solid #e5e7eb;
    border-radius: 12px;
    font-size: 1rem;
    box-sizing: border-box;
    transition: border-color 0.2s;

    &:focus {
      outline: none;
      border-color: #06b6d4;
    }

    &::placeholder {
      color: #9ca3af;
    }
  }
`;

const DoctorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
  gap: 1.25rem;
`;

const DoctorCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border: 2px solid #f1f5f9;
  transition: all 0.2s;

  &:hover {
    border-color: #06b6d4;
    box-shadow: 0 4px 16px rgba(6, 182, 212, 0.1);
  }
`;

const DoctorCardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const DoctorAvatar = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #06b6d4, #0891b2);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
`;

const DoctorName = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  flex: 1;
`;

const DoctorInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: #64748b;

  svg {
    color: #9ca3af;
    flex-shrink: 0;
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 0.5rem;
  padding-top: 1rem;
  border-top: 1px solid #f1f5f9;
`;

const IconBtn = styled.button`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.875rem;
  border: none;
  border-radius: 8px;
  font-size: 0.8125rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &.edit {
    background: #eff6ff;
    color: #2563eb;
    &:hover { background: #dbeafe; }
  }

  &.delete {
    background: #fef2f2;
    color: #dc2626;
    &:hover { background: #fee2e2; }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 4rem 2rem;
  color: #94a3b8;

  svg {
    margin-bottom: 1rem;
    opacity: 0.4;
  }

  h3 {
    font-size: 1.25rem;
    font-weight: 600;
    color: #64748b;
    margin: 0 0 0.5rem 0;
  }

  p {
    margin: 0;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1100;
  backdrop-filter: blur(4px);
`;

const ModalContent = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 20px;
  width: 90%;
  max-width: 480px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from { transform: translateY(20px); opacity: 0; }
    to { transform: translateY(0); opacity: 1; }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h2`
  color: #1e293b;
  font-size: 1.375rem;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  color: #64748b;
  transition: color 0.2s;

  &:hover { color: #1e293b; }
`;

const FormGroup = styled.div`
  margin-bottom: 1.25rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const InputField = styled.input`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: #06b6d4;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &.cancel {
    background: #f1f5f9;
    color: #475569;
    &:hover { background: #e2e8f0; }
  }

  &.save {
    background: #06b6d4;
    color: white;
    &:hover { background: #0891b2; }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }

  &.danger {
    background: #dc2626;
    color: white;
    &:hover { background: #b91c1c; }
  }
`;

const SuccessMsg = styled.div`
  background: #d1fae5;
  border: 1px solid #a7f3d0;
  color: #065f46;
  padding: 0.75rem 1rem;
  border-radius: 12px;
  margin-bottom: 1.25rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 500;
  font-size: 0.875rem;
`;

const StatsBar = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const StatPill = styled.div`
  background: white;
  border: 2px solid #f1f5f9;
  border-radius: 12px;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  color: #475569;
  font-weight: 500;

  span {
    font-weight: 700;
    color: #06b6d4;
    margin-right: 0.25rem;
  }
`;

const DoctorsPage = () => {
  const { t } = useTranslation();
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [form, setForm] = useState({ name: '', phoneNumber: '', address: '', city: '', country: '' });
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);

  const fetchDoctors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/doctors', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setDoctors(response.data.doctors);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDoctors();
  }, []);

  const openEditModal = (doctor) => {
    setEditingDoctor(doctor);
    setForm({
      name: doctor.name || '',
      phoneNumber: doctor.phoneNumber || '',
      address: doctor.address || '',
      city: doctor.city || '',
      country: doctor.country || ''
    });
    setSuccessMsg('');
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (editingDoctor) {
        const response = await axios.patch(`/api/doctors/${editingDoctor._id}`, form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setSuccessMsg(`${response.data.doctor.name} updated!`);
          await fetchDoctors();
          setTimeout(() => {
            setShowModal(false);
            setSuccessMsg('');
          }, 1200);
        }
      } else {
        const response = await axios.post('/api/doctors', form, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.success) {
          setSuccessMsg(`${response.data.doctor.name} added!`);
          await fetchDoctors();
          setForm({ name: '', phoneNumber: '', address: '', city: '', country: '' });
          setTimeout(() => {
            setShowModal(false);
            setSuccessMsg('');
          }, 1200);
        }
      }
    } catch (error) {
      console.error('Error saving doctor:', error);
      alert('Failed to save doctor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (doctorId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/doctors/${doctorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setShowDeleteConfirm(null);
      await fetchDoctors();
    } catch (error) {
      console.error('Error deleting doctor:', error);
      alert('Failed to delete doctor');
    }
  };

  const filteredDoctors = doctors.filter(doc =>
    doc.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (doc.phoneNumber && doc.phoneNumber.includes(searchTerm)) ||
    (doc.address && doc.address.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (doc.city && doc.city.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (doc.country && doc.country.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <PageContainer>
      <Header>
        <div className="header-text">
          <h1>{t('doctors.title')}</h1>
          <p>{t('doctors.subtitle')}</p>
        </div>
      </Header>

      <StatsBar>
        <StatPill><span>{doctors.length}</span> {t('doctors.totalDoctors')}</StatPill>
      </StatsBar>

      <SearchBar>
        <Search size={18} />
        <input
          type="text"
          placeholder={t('doctors.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </SearchBar>

      {loading ? (
        <EmptyState><p>Loading doctors...</p></EmptyState>
      ) : filteredDoctors.length === 0 ? (
        <EmptyState>
          <User size={48} />
          <h3>{searchTerm ? t('doctors.noMatch') : t('doctors.noDoctors')}</h3>
          <p>{searchTerm ? 'Try a different search term' : 'Add doctors from the prescription form.'}</p>
        </EmptyState>
      ) : (
        <DoctorGrid>
          {filteredDoctors.map(doctor => (
            <DoctorCard key={doctor._id}>
              <DoctorCardHeader>
                <DoctorAvatar>
                  <User size={22} />
                </DoctorAvatar>
                <DoctorName>{doctor.name}</DoctorName>
              </DoctorCardHeader>

              <DoctorInfo>
                <InfoRow>
                  <Phone size={14} />
                  {doctor.phoneNumber || t('doctors.noPhone')}
                </InfoRow>
                <InfoRow>
                  <MapPin size={14} />
                  {doctor.address || t('doctors.noAddress')}
                </InfoRow>
                <InfoRow>
                  <MapPin size={14} />
                  {[doctor.city, doctor.country].filter(Boolean).join(', ') || t('doctors.noCity')}
                </InfoRow>
              </DoctorInfo>

              <CardActions>
                <IconBtn className="edit" onClick={() => openEditModal(doctor)}>
                  <Edit2 size={14} />
                  {t('doctors.edit')}
                </IconBtn>
                <IconBtn className="delete" onClick={() => setShowDeleteConfirm(doctor._id)}>
                  <Trash2 size={14} />
                  {t('doctors.remove')}
                </IconBtn>
              </CardActions>

              {showDeleteConfirm === doctor._id && (
                <div style={{
                  marginTop: '0.75rem',
                  padding: '0.75rem',
                  background: '#fef2f2',
                  borderRadius: '8px',
                  fontSize: '0.875rem'
                }}>
                  <p style={{ margin: '0 0 0.5rem 0', color: '#991b1b', fontWeight: 500 }}>
                    Remove {doctor.name}?
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <ModalBtn
                      className="danger"
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.8125rem' }}
                      onClick={() => handleDelete(doctor._id)}
                    >
                      Yes, Remove
                    </ModalBtn>
                    <ModalBtn
                      className="cancel"
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.8125rem' }}
                      onClick={() => setShowDeleteConfirm(null)}
                    >
                      Cancel
                    </ModalBtn>
                  </div>
                </div>
              )}
            </DoctorCard>
          ))}
        </DoctorGrid>
      )}

      {showModal && (
        <ModalOverlay onClick={() => { setShowModal(false); setSuccessMsg(''); }}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>
                <UserPlus size={22} color="#06b6d4" />
                {editingDoctor ? t('doctors.editDoctor') : t('doctors.addDoctor')}
              </ModalTitle>
              <CloseBtn onClick={() => { setShowModal(false); setSuccessMsg(''); }}>
                <X size={22} />
              </CloseBtn>
            </ModalHeader>

            {successMsg && (
              <SuccessMsg>
                <CheckCircle size={16} />
                {successMsg}
              </SuccessMsg>
            )}

            <FormGroup>
              <Label>{t('doctors.name')} *</Label>
              <InputField
                type="text"
                placeholder="Dr. Doctor's full name"
                value={form.name}
                onChange={(e) => {
                  const value = e.target.value.replace(/[^a-zA-Z\s'.-]/g, '');
                  setForm(prev => ({ ...prev, name: value }));
                }}
              />
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <Phone size={13} /> Phone Number
              </Label>
              <InputField
                type="tel"
                placeholder="Optional"
                value={form.phoneNumber}
                onChange={(e) => setForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
              />
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <MapPin size={13} /> Address
              </Label>
              <InputField
                type="text"
                placeholder="Optional"
                value={form.address}
                onChange={(e) => setForm(prev => ({ ...prev, address: e.target.value }))}
              />
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <MapPin size={13} /> City
              </Label>
              <InputField
                type="text"
                placeholder="Optional"
                value={form.city}
                onChange={(e) => setForm(prev => ({ ...prev, city: e.target.value }))}
              />
            </FormGroup>

            <FormGroup>
              <Label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                <MapPin size={13} /> Country
              </Label>
              <InputField
                type="text"
                placeholder="Optional"
                value={form.country}
                onChange={(e) => setForm(prev => ({ ...prev, country: e.target.value }))}
              />
            </FormGroup>

            <ModalActions>
              <ModalBtn
                className="cancel"
                onClick={() => { setShowModal(false); setSuccessMsg(''); }}
              >
                Cancel
              </ModalBtn>
              <ModalBtn
                className="save"
                onClick={handleSave}
                disabled={!form.name.trim() || saving}
              >
                {saving ? 'Saving...' : editingDoctor ? 'Update Doctor' : 'Save Doctor'}
              </ModalBtn>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </PageContainer>
  );
};

export default DoctorsPage;
